import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useOutletContext } from "react-router-dom";
import { Check, ShoppingBag } from "lucide-react";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import FloatingButtons from "../../components/FloatingButtons";
import AuthModal from "../../components/AuthModal";
import { useCustomerAuth } from "../../lib/CustomerAuthContext";
import { cn } from "../../lib/utils";
import { TURKEY_LOCATIONS } from "../../data/turkeyLocations";
import { todayISO } from "./pickers";

const STEPS = [
  { key: "cart", path: "/sepet", label: "Sepetim" },
  { key: "delivery", path: "/sepet/teslimat", label: "Teslimat Bilgileri" },
  { key: "review", path: "/sepet/onayla", label: "Onayla" },
];

function stepKeyForPath(pathname) {
  if (pathname.startsWith("/sepet/teslimat")) return "delivery";
  if (pathname.startsWith("/sepet/onayla")) return "review";
  if (pathname.startsWith("/sepet/tesekkurler")) return "done";
  if (pathname.startsWith("/sepet/odeme")) return "done";
  return "cart";
}

function CheckoutStepper({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <div className="border-b border-[#EAEAEA] bg-white">
      <div className="frida-container py-6 flex items-center justify-center gap-3 sm:gap-5">
        {STEPS.map((s, i) => {
          const isDone = idx > i || current === "done";
          const isCurrent = i === idx;
          return (
            <div key={s.key} className="flex items-center gap-3 sm:gap-5">
              <div className="flex items-center gap-2.5">
                <span
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 transition-colors font-serif",
                    isDone || isCurrent ? "bg-[#A85C34] text-white" : "bg-[#F1E4D6] text-[#A85C34]/60"
                  )}
                >
                  {isDone && !isCurrent ? <Check className="w-3.5 h-3.5" /> : i + 1}
                </span>
                <span className={cn("text-xs sm:text-sm font-medium hidden sm:inline", isCurrent ? "text-[#222222]" : "text-[#888888]")}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && <span className="w-8 sm:w-16 h-px bg-[#EAEAEA]" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const EMPTY_FORM = {
  name: "", phone: "", email: "",
  il: "", ilce: "", addressDetail: "",
  note: "", senderName: "",
  deliveryDate: "", deliveryStart: "", deliveryEnd: "",
};

export default function CartLayout() {
  const location = useLocation();
  const { customer, isLoggedIn } = useCustomerAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [notesOpen, setNotesOpen] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountMsg, setDiscountMsg] = useState("");
  const [authOpen, setAuthOpen] = useState(false);
  const [zoneInfo, setZoneInfo] = useState(null);
  const [zoneLoading, setZoneLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [slotError, setSlotError] = useState("");

  const ilceOptions = TURKEY_LOCATIONS.find((p) => p.il === form.il)?.ilceler ?? [];
  const isDeliveryToday = form.deliveryDate === todayISO();

  const allSlotsPassedToday = (() => {
    const slots = zoneInfo?.slots ?? [];
    if (!isDeliveryToday || slots.length === 0) return false;
    const nowHHMM = new Date().toTimeString().slice(0, 5);
    return slots.every((s) => s.start_time <= nowHHMM);
  })();

  // İl/ilçe seçilince, o bölge için panelde tanımlı kesim saati/aynı gün
  // teslim durumu/saat aralıklarını sorar — sipariş tarihi ve saat seçeneği
  // buna göre otomatik doldurulur. Her işletme için bu uç nokta kendi
  // panelinde girdiği bölgeleri döndürür, sabit/ortak bir kural yoktur.
  useEffect(() => {
    if (!form.il) { setZoneInfo(null); return; }
    setZoneLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams({ il: form.il, ilce: form.ilce || "" });
      fetch(`/api/delivery-zone-lookup.php?${params.toString()}`)
        .then((r) => r.json())
        .then((data) => setZoneInfo(data))
        .catch(() => setZoneInfo(null))
        .finally(() => setZoneLoading(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.il, form.ilce]);

  useEffect(() => {
    if (!zoneInfo) return;
    setForm((f) => {
      if (f.deliveryDate && f.deliveryDate >= zoneInfo.earliest_date) return f;
      return { ...f, deliveryDate: zoneInfo.earliest_date };
    });
  }, [zoneInfo]);

  useEffect(() => {
    if (!isLoggedIn || !customer) return;
    // GÜVENLİK/DENETİM DÜZELTMESİ: "name"/"phone" alanları formda AÇIKÇA
    // "Alıcı Adı Soyadı" (teslimat alıcısı) olarak etiketleniyor — buraya
    // giriş yapmış ALICININ KENDİ bilgilerini otomatik doldurmak, biri
    // başkasına (örn. annesine) çiçek gönderirken fark etmeyip
    // düzeltmezse siparişin YANLIŞ isme/telefonla teslim edilmesine yol
    // açardı. Yalnızca e-posta otomatik dolduruluyor — o alan sipariş
    // onayının gönderileceği adres (alıcı değil, sipariş VEREN kişi).
    setForm((f) => ({
      ...f,
      email: f.email || customer.email || "",
    }));
  }, [isLoggedIn, customer]);

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const handleIlChange = (e) => setForm((f) => ({ ...f, il: e.target.value, ilce: "", deliveryStart: "", deliveryEnd: "" }));
  const handleSlotSelect = (start, end) => {
    setForm((f) => ({ ...f, deliveryStart: start, deliveryEnd: end }));
    setSlotError("");
  };
  const appendEmoji = (emoji) => setForm((f) => ({ ...f, note: `${f.note}${f.note ? " " : ""}${emoji}` }));
  const applyDiscount = () => {
    if (!discountCode.trim()) return;
    setDiscountMsg("İndirim kodu özelliği yakında aktif olacak.");
  };
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setZoneInfo(null);
    setDateError("");
    setSlotError("");
    setNotesOpen(false);
    setDiscountCode("");
    setDiscountMsg("");
  };

  const ctx = {
    form, setForm, handleChange, handleIlChange, handleSlotSelect, appendEmoji, resetForm,
    ilceOptions, isDeliveryToday, allSlotsPassedToday,
    notesOpen, setNotesOpen,
    discountCode, setDiscountCode, discountMsg, setDiscountMsg, applyDiscount,
    authOpen, setAuthOpen,
    zoneInfo, zoneLoading,
    dateError, setDateError, slotError, setSlotError,
  };

  const currentStep = stepKeyForPath(location.pathname);

  return (
    <div className="min-h-screen bg-white text-[#222222]" data-testid="cart-flow">
      <Header />

      <div className="bg-[#FBF9F4] border-b border-[#EAEAEA]">
        <div className="frida-container py-5 flex items-center gap-2.5">
          <ShoppingBag className="w-4 h-4 text-[#A85C34]" />
          <Link to="/" className="text-xs text-[#888888] hover:text-[#A85C34] transition-colors">Ana Sayfa</Link>
          <span className="text-xs text-[#CBBBAA]">/</span>
          <span className="text-xs text-[#222222] font-medium">Sepet</span>
        </div>
      </div>

      {currentStep !== "done" && <CheckoutStepper current={currentStep} />}

      <div className="frida-container py-10 md:py-14">
        <Outlet context={ctx} />
      </div>

      <Footer />
      <FloatingButtons />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} onSuccess={() => setAuthOpen(false)} />
    </div>
  );
}

export function useCartFlow() {
  return useOutletContext();
}
